<!-- Adapted from YesPlayMusic v0.4.11 src/components/ContextMenu.vue (MIT, Copyright (c) 2020-2023 qier222). Template and SCSS retained; Vuex dependencies replaced with browser focus and viewport handling. -->
<template>
  <div ref="contextMenu" class="context-menu" :class="{ 'salt-context-menu': workspace.appearance === 'salt' }">
    <div v-if="showMenu" ref="menu" class="menu" role="menu" tabindex="-1"
      :style="{ top, left }" @blur.capture="onBlur" @click="closeMenu" @keydown="onKeydown">
      <SaltMaterialSurface v-if="workspace.appearance === 'salt'" />
      <slot></slot>
    </div>
  </div>
</template>
<script>
import { workspace } from '../workspace';
import SaltMaterialSurface from '../salt/ui/SaltMaterialSurface.vue';
export default {
  name: 'ContextMenu', components: { SaltMaterialSurface },
  data() { return { workspace, showMenu: false, top: '0px', left: '0px' }; },
  mounted() { document.addEventListener('pointerdown', this.onOutsidePointer); },
  beforeDestroy() { document.removeEventListener('pointerdown', this.onOutsidePointer); },
  methods: {
    setMenu(top, left) {
      const heightOffset = document.querySelector('.player') ? 64 : 0;
      this.top = Math.max(8, Math.min(top, window.innerHeight - this.$refs.menu.offsetHeight - heightOffset - 8)) + 'px';
      this.left = Math.max(8, Math.min(left, window.innerWidth - this.$refs.menu.offsetWidth - 25)) + 'px';
    },
    closeMenu(restoreFocus = true) {
      const wasOpen = this.showMenu;
      this.showMenu = false;
      if (wasOpen && restoreFocus && this._opener?.isConnected) this._opener.focus();
    },
    onOutsidePointer(event) { if (this.showMenu && !this.$refs.menu?.contains(event.target)) this.closeMenu(false); },
    onBlur(event) { if (!this.$refs.menu?.contains(event.relatedTarget)) this.closeMenu(false); },
    openMenu(event) {
      this._opener = event.currentTarget || document.activeElement;
      const bounds = this._opener?.getBoundingClientRect?.();
      const top = event.clientY || bounds?.bottom || 8;
      const left = event.clientX || bounds?.left || 8;
      this.showMenu = true;
      this.$nextTick(() => { this.setMenu(top, left); this.$refs.menu.focus(); });
      event.preventDefault();
    },
    onKeydown(event) {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.closeMenu(); return; }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      const items = Array.from(this.$refs.menu.querySelectorAll('[role="menuitem"]:not([disabled])'));
      if (!items.length) return;
      event.preventDefault();
      const index = items.indexOf(document.activeElement);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : event.key === 'ArrowDown' ? (index + 1) % items.length : (index - 1 + items.length) % items.length;
      items[next].focus();
    },
  },
};
</script>
<style lang="scss" scoped>
.context-menu {
  width: 100%;
  height: 100%;
  user-select: none;
}

.menu {
  position: fixed;
  min-width: 136px;
  max-width: 240px;
  list-style: none;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 6px 12px -4px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(12px);
  border-radius: 12px;
  box-sizing: border-box;
  padding: 6px;
  z-index: 1000;
  -webkit-app-region: no-drag;
  transition: background 125ms ease-out, opacity 125ms ease-out,
    transform 125ms ease-out;

  &:focus {
    outline: none;
  }
}

[data-theme='dark'] {
  .menu {
    background: rgba(36, 36, 36, 0.78);
    backdrop-filter: blur(16px) contrast(120%) brightness(60%);
    border: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 6px rgba(255, 255, 255, 0.08);
  }
  .menu .item:hover {
    color: var(--color-text);
  }
}

@supports (-moz-appearance: none) {
  .menu {
    background-color: var(--color-body-bg) !important;
  }
}

.menu .item {
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  font-family: inherit;
  font-weight: 600;
  font-size: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  cursor: default;
  color: var(--color-text);
  display: flex;
  align-items: center;
  &:hover, &:focus-visible {
    color: var(--color-primary);
    background: var(--color-primary-bg-for-transparent);
    transition: opacity 125ms ease-out, transform 125ms ease-out;
  }
  &:active {
    opacity: 0.75;
    transform: scale(0.95);
  }

  .svg-icon {
    height: 16px;
    width: 16px;
    margin-right: 5px;
  }
}

hr {
  margin: 4px 10px;
  background: rgba(128, 128, 128, 0.18);
  height: 1px;
  box-shadow: none;
  border: none;
}

.item-info {
  padding: 10px 10px;
  display: flex;
  align-items: center;
  color: var(--color-text);
  cursor: default;
  img {
    height: 38px;
    width: 38px;
    border-radius: 4px;
  }
  .info {
    margin-left: 10px;
  }
  .title {
    font-size: 16px;
    font-weight: 600;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    overflow: hidden;
    word-break: break-all;
  }
  .subtitle {
    font-size: 12px;
    opacity: 0.68;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    overflow: hidden;
    word-break: break-all;
  }
}

/* Salt PopupMenu uses its opaque popup color, without material blur. */
.salt-context-menu .menu { min-width: 180px; max-width: 280px; padding: 4px 0; border: 1px solid var(--salt-stroke); border-radius: var(--salt-radius-medium,6px); color: var(--salt-text); background: transparent !important; isolation: isolate; -webkit-backdrop-filter: none; backdrop-filter: none; box-shadow: none; transition: opacity 150ms ease; }
.salt-context-menu .menu ::v-deep(.item) { min-height: 40px; padding: 12px 16px; gap: 12px; border-radius: 0; color: var(--salt-text); font-size: 14px; font-weight: 400; line-height: normal; transform: none; opacity: 1; }
.salt-context-menu .menu ::v-deep(.item:hover), .salt-context-menu .menu ::v-deep(.item:focus-visible) { color: var(--salt-text); background: var(--salt-hover-overlay); outline: none; transform: none; }
.salt-context-menu .menu ::v-deep(.item:active) { background: var(--salt-press-overlay); transform: none; opacity: 1; }
.salt-context-menu .menu ::v-deep(hr) { margin: 4px 16px; background: var(--salt-stroke); }
.salt-context-menu .menu ::v-deep(.item-info) { padding: 12px 16px; color: var(--salt-text); }
.salt-context-menu .menu ::v-deep(.item-info .title) { font-size: 14px; font-weight: 400; }
.salt-context-menu .menu ::v-deep(.item-info .subtitle) { color: var(--salt-subtext); opacity: 1; }
</style>

